/**
 * Advisory Presentation
 *
 * The shared, pure title/status contract both the list row and the detail
 * scaffold consume. No UI-library import and no i18n import — this module
 * returns keys and options for a caller to translate, never a translated
 * string. Keeping it pure is what guarantees the list row and the detail
 * route can never disagree about what a title or a status line says.
 */

import type { AdvisoryStage, GoalAttempt } from '../../redux/slices/advisory/advisoryTypes';

/**
 * The data-level truncation cap, applied in addition to CSS ellipsis
 * truncation — this cap bounds the DOM text node and any `aria-label`
 * themselves, so an unbounded agent-authored string never reaches either.
 */
export const GOAL_TITLE_MAX_LENGTH = 80;

/**
 * Derives a row/scaffold title from a document's `goal` attempts. Only
 * `goal[0]` is ever read — later attempts never change the result,
 * matching the "title = goal[0].goal" rule (the field is `goal`, not
 * `text` — see `GoalAttempt`'s own doc comment).
 *
 * Falls back to the original query when there is no usable first-attempt text.
 * Returns `null` when neither has text, so the caller renders
 * `advisory:stage.titlePlaceholder`. Returning `null` rather than
 * the placeholder key itself keeps this module free of i18n knowledge and
 * keeps the placeholder decision at the render site.
 *
 * This function is deliberately status-agnostic: it extends to ANY status
 * with an empty/blank first attempt, not just `extracting`, so no status
 * field is ever inspected here.
 */
export function truncateGoalTitle(goal: GoalAttempt[], originalQuery?: string): string | null {
  const text = goal[0]?.goal?.trim() ? goal[0].goal : originalQuery;
  if (!text || !text.trim()) {
    return null;
  }

  if (text.length <= GOAL_TITLE_MAX_LENGTH) {
    return text;
  }

  // Cut at the cap, then back up to the last whole-word boundary at or
  // before it. A single unbroken token with no boundary before the cap
  // falls back to a hard cut — bounded either way, never unbounded.
  const window = text.slice(0, GOAL_TITLE_MAX_LENGTH);
  const lastBoundary = window.lastIndexOf(' ');
  const base = lastBoundary > 0 ? window.slice(0, lastBoundary) : window;

  return `${base.trimEnd()}…`;
}

/**
 * A translation-ready status line for a document tag: a fixed i18n key plus
 * the interpolation values read straight off the stage's own payload, never
 * hardcoded.
 *
 * Returns `null` for the four channel-level tags (`loading`, `denied`,
 * `channelFailed`, `absent`) — those are callout states, not status lines.
 *
 * The trailing `: never` assignment is an exhaustiveness guard: a future
 * fifteenth `AdvisoryStage` variant fails to compile here instead of silently
 * falling through to `undefined`. It has already earned its keep — the five
 * `analysis*` variants could not be added without this line naming every place
 * that had to learn about them.
 *
 * The four `analysis*` variants that carry counts reuse the `attempt` /
 * `maxIterations` interpolation names rather than inventing `completedTurns` /
 * `plannedTurns` ones. That is not laziness: this function's return type declares
 * exactly one options shape, and widening it to a union would make every caller
 * branch on which stage it was handed to know what it may interpolate. The i18n
 * copy names the quantity, so nothing renders the word "attempt".
 */
export function stageStatusLine(
  stage: AdvisoryStage
): { key: string; options?: { attempt: number; maxIterations: number } } | null {
  switch (stage.tag) {
    case 'loading':
    case 'denied':
    case 'channelFailed':
    case 'absent':
      return null;
    case 'reviewing':
      return { key: 'advisory:stage.reviewing' };
    case 'extracting':
      return { key: 'advisory:stage.extracting' };
    case 'awaitingCompanyConfirmation':
      return { key: 'advisory:stage.awaitingCompanyConfirmation' };
    case 'awaitingCounterpartyConfirmation':
      return { key: 'advisory:stage.awaitingCounterpartyConfirmation' };
    case 'researchingCounterparties':
      return { key: 'advisory:stage.researchingCounterparties' };
    case 'awaitingConfirmation':
      return {
        key: 'advisory:stage.awaitingConfirmation',
        options: { attempt: stage.attempt, maxIterations: stage.maxIterations },
      };
    case 'refinementExhausted':
      return {
        key: 'advisory:stage.refinementExhausted',
        options: { attempt: stage.attempt, maxIterations: stage.maxIterations },
      };
    case 'confirmed':
      return { key: 'advisory:stage.confirmed' };
    case 'extractionFailed':
      return { key: 'advisory:stage.extractionFailed' };
    case 'analysisPrepared':
      return { key: 'advisory:stage.analysisPrepared' };
    case 'analysisRunning':
      return {
        key: 'advisory:stage.analysisRunning',
        options: { attempt: stage.completedTurns, maxIterations: stage.plannedTurns },
      };
    case 'analysisComplete':
      return {
        key: 'advisory:stage.analysisComplete',
        options: { attempt: stage.completedTurns, maxIterations: stage.plannedTurns },
      };
    case 'analysisPartial':
      return {
        key: 'advisory:stage.analysisPartial',
        options: { attempt: stage.completedTurns, maxIterations: stage.plannedTurns },
      };
    case 'analysisFailed':
      return { key: 'advisory:stage.analysisFailed' };
    default: {
      const exhaustive: never = stage;
      return exhaustive;
    }
  }
}

/**
 * The refinement budget is worded as what remains, in refinements —
 * `Math.max(0, stage.maxIterations - stage.attempt)`, clamped so a stage
 * payload the client should never see (attempt already at or past the cap)
 * can never read as a negative count.
 *
 * "Attempt N of M" was rejected for the goal-review surface because
 * `attempt` is `goal[].length`: the machine's own first extraction already
 * counts as attempt 1, so telling the user they had "used" an attempt they
 * never touched would misdescribe what happened. The shipped
 * `advisory:stage.awaitingConfirmation` key keeps that attempt/max phrasing
 * for the *list row* status line — a different surface, deliberately not
 * changed by this function.
 *
 * Reads only the two fields it needs so it accepts either the
 * `awaitingConfirmation` or the `refinementExhausted` stage variant without
 * narrowing — the caller decides which variant to render from, this
 * function only does the arithmetic.
 */
export function refinementsRemaining(stage: { attempt: number; maxIterations: number }): number {
  return Math.max(0, stage.maxIterations - stage.attempt);
}

/**
 * The key-question step's floor, as a pure client-owned constant.
 * Deliberately, this module exports no upper bound sibling to this
 * constant anywhere — the count of key questions is driven by the
 * engagement the agent matched, so a client-side ceiling could reject a
 * legitimate engagement whose matched type simply carries more questions
 * than any number picked here in advance.
 */
export const KEY_QUESTION_MIN = 1;

/**
 * Whether the key-questions step's confirm action is enabled — true at or
 * above the floor, true at any count above it, with no upper bound. Takes
 * a count, not an array, so no caller can smuggle a ceiling in through it.
 */
export function canConfirmKeyQuestions(count: number): boolean {
  return count >= KEY_QUESTION_MIN;
}

/**
 * A REMOVAL floor, distinct from the confirm floor above. The two are
 * deliberately different comparisons against the same `KEY_QUESTION_MIN`
 * constant — `>` here, `>=` for `canConfirmKeyQuestions` — because the
 * confirm gate answers "is this list acceptable", while this gate answers
 * "may this list get smaller". Takes a count, not an array, for the same
 * reason `canConfirmKeyQuestions` does.
 *
 * This floor exists because an earlier client change deleted the client's
 * only add path (the key-question editor's add field). Before that
 * deletion, a user who removed every key question could recover by adding
 * one back; after it, dropping to zero has no recovery until a
 * propose/accept flow is reintroduced. Removals are real `deleteDoc`
 * writes, so Back → fork → re-entry restores nothing once they have fired.
 *
 * Once removal moves onto the supervisor's `removeQuestions` endpoint,
 * this predicate should be reused rather than re-derived.
 */
export function canRemoveKeyQuestion(count: number): boolean {
  return count > KEY_QUESTION_MIN;
}

/**
 * The shape a `projectId` must have before it may be interpolated into a
 * Firestore path.
 *
 * A Firestore document id is a single path segment. React Router DECODES
 * path params, so `/advisory/a%2Fb%2Fc` yields `projectId === 'a/b/c'` — a
 * value that silently re-targets the reference at a different location
 * inside the same user's subtree, and, when the resulting segment count is
 * odd, makes the Firestore SDK throw SYNCHRONOUSLY inside the listener
 * setup. That throw happens inside the async `startEngagementListeners`
 * thunk the mount effect dispatches with no `.catch`, i.e. an unhandled
 * rejection with the page stuck on its loading skeleton.
 *
 * There is no cross-tenant exposure to close here — the `users/${userId}`
 * prefix always comes from authenticated state, never from the URL — so
 * this is a robustness and honesty fix, not a privilege boundary. It also
 * makes `advisoryService.ts`'s path invariant TRUE: before this, that
 * module's doc comment claimed all three path segments were server-sourced
 * while `projectId` came straight from `useParams`.
 *
 * Deliberately conservative: `/` and `.` are excluded (path separators and
 * Firestore's own relative-path tokens), and the 128-character bound keeps
 * an absurd URL from ever reaching the SDK. Firestore's auto-generated ids
 * are 20 URL-safe characters, so this rejects nothing the server mints.
 */
const ADVISORY_PROJECT_ID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;

/** True when `value` is safe to interpolate into a Firestore path segment. */
export function isValidAdvisoryProjectId(value: string | undefined): value is string {
  return value !== undefined && ADVISORY_PROJECT_ID_PATTERN.test(value);
}
